import { useAuthFetch } from "@/hooks/useAuthFetch";
import { config } from "@/lib/config";
import { useUIStore } from "@/stores/ui-store";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/utils/toast";
import type { BookingFlowWebhooks } from "@/types/webhooks";
import type { EmailConfig } from "@/types";

export interface BookingFlow {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  duration: number;
  timezone: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  teamMembers: {
    id: string;
    name: string;
    email: string;
  }[];
  meetingDuration: number;
  bufferTime: number;
  advanceBookingDays: number;
  minNoticeHours: number;
  assignmentStrategy: "first_available" | "round_robin" | "priority";
  requireAllAvailable: boolean;
  features?: any; // JSONB field for features configuration
  questions?: any; // JSONB field for questions configuration
  contactFields?: any; // JSONB field for contact fields configuration
  routing?: any; // JSONB field for routing/qualification configuration
  webhooks?: BookingFlowWebhooks | null; // JSONB field for webhook configuration
  emailConfig?: EmailConfig | null; // JSONB field for email customization
}

export const bookingFlowsKeys = {
  all: (tenant: string) => ["booking-flows", tenant] as const,
  lists: (tenant: string) => [...bookingFlowsKeys.all(tenant), "list"] as const,
  list: (tenant: string, filters: string) =>
    [...bookingFlowsKeys.lists(tenant), { filters }] as const,
  details: (tenant: string) =>
    [...bookingFlowsKeys.all(tenant), "detail"] as const,
  detail: (tenant: string, slug: string) =>
    [...bookingFlowsKeys.details(tenant), slug] as const,
};

export function useBookingFlows() {
  const authFetch = useAuthFetch();
  const currentTenant = useUIStore((state) => state.currentTenant);

  return useQuery<BookingFlow[]>({
    queryKey: currentTenant ? bookingFlowsKeys.list(currentTenant, "all") : [],
    queryFn: async () => {
      const response = await authFetch(`${config.apiAdminUrl}/booking-flows`);
      if (!response.ok) {
        throw new Error("Failed to fetch booking flows");
      }
      const data = await response.json();
      return data.bookingFlows;
    },
    enabled: !!currentTenant,
  });
}

export function useBookingFlow(slug: string, enabled: boolean) {
  const authFetch = useAuthFetch();
  const currentTenant = useUIStore((state) => state.currentTenant);

  return useQuery<BookingFlow>({
    queryKey: currentTenant ? bookingFlowsKeys.detail(currentTenant, slug) : [],
    queryFn: async () => {
      const response = await authFetch(
        `${config.apiAdminUrl}/booking-flows/${slug}`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch booking flow");
      }
      const data = await response.json();
      return data.bookingFlow;
    },
    enabled: !!currentTenant && enabled,
  });
}

export function useCreateBookingFlow() {
  const authFetch = useAuthFetch();
  const queryClient = useQueryClient();
  const currentTenant = useUIStore((state) => state.currentTenant);

  return useMutation({
    mutationFn: async (data: Partial<BookingFlow>) => {
      const response = await authFetch(`${config.apiAdminUrl}/booking-flows`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Failed to create booking flow");
      }
      return response.json();
    },
    onSuccess: () => {
      if (currentTenant) {
        queryClient.invalidateQueries({
          queryKey: bookingFlowsKeys.lists(currentTenant),
        });
      }
      toast.success("Booking flow created successfully");
    },
    onError: () => {
      toast.error("Failed to create booking flow");
    },
  });
}

export function useUpdateBookingFlow() {
  const authFetch = useAuthFetch();
  const queryClient = useQueryClient();
  const currentTenant = useUIStore((state) => state.currentTenant);

  return useMutation({
    mutationFn: async ({
      slug,
      data,
    }: {
      slug: string;
      data: Partial<BookingFlow>;
    }) => {
      const response = await authFetch(
        `${config.apiAdminUrl}/booking-flows/${slug}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );
      if (!response.ok) {
        throw new Error("Failed to update booking flow");
      }
      return response.json();
    },
    onSuccess: (_data, variables) => {
      if (currentTenant) {
        queryClient.invalidateQueries({
          queryKey: bookingFlowsKeys.lists(currentTenant),
        });
        queryClient.invalidateQueries({
          queryKey: bookingFlowsKeys.detail(currentTenant, variables.slug),
        });
      }
      toast.success("Booking flow updated successfully");
    },
    onError: () => {
      toast.error("Failed to update booking flow");
    },
  });
}

export function useDeleteBookingFlow() {
  const authFetch = useAuthFetch();
  const queryClient = useQueryClient();
  const currentTenant = useUIStore((state) => state.currentTenant);

  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await authFetch(
        `${config.apiAdminUrl}/booking-flows/${slug}`,
        {
          method: "DELETE",
        },
      );
      if (!response.ok) {
        throw new Error("Failed to delete booking flow");
      }
      return response.json();
    },
    onSuccess: () => {
      if (currentTenant) {
        queryClient.invalidateQueries({
          queryKey: bookingFlowsKeys.lists(currentTenant),
        });
      }
      toast.success("Booking flow deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete booking flow");
    },
  });
}
